/**
 * SanPlumb — ViewerScreen (Fase 3d)
 *
 * Ensambla la pantalla del visor: LeftPanel + canvas (Viewer3D + overlays) +
 * RightPanel, en el grid de 3 columnas (232px / 1fr / 280px).
 *
 * Genera el SystemData UNA vez por estrategia (`systemForRouting`) y lo
 * comparte con el visor (render) y el RightPanel (BOM/info), garantizando
 * que ambos vean exactamente la misma red.
 */

import { useMemo } from 'react';
import type {
  CameraView,
  LayerState,
  RightTab,
  SelectedElement,
  BrandState,
  UsoMode,
  CountsState,
  RoomDims,
} from '../../types/ui';
import type { RoutingStrategy } from '../../types';
import type { TipologiaCode } from '../../constants/tipologias';
import { systemForRouting } from '../../engine/three/generateSystem';
import { Viewer3D } from './Viewer3D';
import { ViewControls, LayerToolbar } from './ViewerControls';
import { LeftPanel } from '../panels/LeftPanel';
import { RightPanel } from '../panels/RightPanel';

export interface ViewerScreenProps {
  routing: RoutingStrategy;
  tipologia: TipologiaCode;
  roomDims: RoomDims | null;
  layers: LayerState;
  view: CameraView;
  animKey: number;
  tab: RightTab;
  selected: SelectedElement;
  counts: CountsState;
  mode: UsoMode;
  brand: BrandState;
  /** Pausa / reanuda la auto-rotación de la cámara. */
  autoRotate: boolean;
  onToggleAutoRotate: () => void;
  onSetView: (v: CameraView) => void;
  onToggleLayer: (k: keyof LayerState) => void;
  onSetTab: (t: RightTab) => void;
  onSelect: (el: SelectedElement) => void;
  setBrand: (b: BrandState) => void;
}

export function ViewerScreen({
  routing,
  tipologia,
  roomDims,
  layers,
  view,
  animKey,
  tab,
  selected,
  counts,
  mode,
  brand,
  autoRotate,
  onToggleAutoRotate,
  onSetView,
  onToggleLayer,
  onSetTab,
  onSelect,
  setBrand,
}: ViewerScreenProps): JSX.Element {
  // El SystemData se recalcula cuando cambian counts, tipología, dims del
  // recinto, modo, estrategia o animKey. Las dimensiones declaradas por
  // el usuario en la ingesta (`roomDims`) tienen prioridad sobre las
  // sugeridas por la tipología §XI — los muros virtuales se construyen
  // contra los valores del usuario.
  const system = useMemo(
    () => systemForRouting(routing, counts, tipologia, mode, roomDims ?? undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routing, counts, tipologia, mode, roomDims, animKey],
  );

  return (
    <div className="viewer screen-anim" data-screen-label="03 Visor 3D">
      <LeftPanel counts={counts} mode={mode} brand={brand} setBrand={setBrand} />

      <div className="canvas-wrap">
        <Viewer3D
          system={system}
          layers={layers}
          view={view}
          animKey={animKey}
          autoRotate={autoRotate}
          onSelect={onSelect}
        />

        {/* Overlay top-left: capas */}
        <div className="cv-overlay tl">
          <LayerToolbar layers={layers} onToggle={onToggleLayer} />
        </div>

        {/* Overlay top-right: vistas */}
        <div className="cv-overlay tr">
          <ViewControls
            view={view}
            onView={onSetView}
            autoRotate={autoRotate}
            onToggleAutoRotate={onToggleAutoRotate}
          />
        </div>

        {/* Overlay bottom-center: leyenda */}
        <div className="cv-overlay bc">
          Arrastra para orbitar · rueda para zoom · click para inspeccionar
        </div>
      </div>

      <RightPanel tab={tab} setTab={onSetTab} selected={selected} system={system} />
    </div>
  );
}
